<div class="ts-page">
  <div class="ts-save-bar">
    <div class="ts-save-bar__inner">
      <div class="ts-save-bar__left">
        <button
          type="button"
          class="ts-save-bar__back"
          [attr.aria-label]="'testSteps.form.backAriaLabel' | translate"
          (click)="onCancel()"
        >
          <mat-icon aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div class="ts-save-bar__heading">
          <span class="ts-save-bar__eyebrow">
            @if (isEdit) {
              {{ 'testSteps.form.eyebrowEdit' | translate: { id: shortId() } }}
            } @else {
              {{ 'testSteps.form.eyebrowNew' | translate }}
            }
          </span>
          <span class="ts-save-bar__title">
            {{ (isEdit ? 'testSteps.form.editTitle' : 'testSteps.form.createTitle') | translate }}
          </span>
        </div>
        @if (isDirty()) {
          <span class="ts-dirty-pill">
            <span class="ts-dirty-pill__dot" aria-hidden="true"></span>
            {{ 'testSteps.form.unsavedChanges' | translate }}
          </span>
        }
      </div>
      <div class="ts-save-bar__right">
        <button mat-stroked-button type="button" (click)="onCancel()">
          {{ 'testSteps.form.cancel' | translate }}
        </button>
        @if (!isReadOnly()) {
          <button mat-flat-button color="primary" type="button" [disabled]="saveDisabled()" (click)="onSave()">
            <mat-icon aria-hidden="true">check</mat-icon>
            {{ 'testSteps.form.save' | translate }}
          </button>
        }
      </div>
    </div>
  </div>

  @if (isArchived()) {
    <app-projekt-archiviert-hinweis class="ts-archiviert-hinweis" />
  } @else if (isProjectReader()) {
    <div class="ts-readonly-hint">
      <mat-icon class="ts-readonly-hint__icon" aria-hidden="true">info</mat-icon>
      <p class="ts-readonly-hint__text">{{ 'testSteps.form.readOnlyHint' | translate }}</p>
    </div>
  }

  @if (loading()) {
    <div class="ts-page__loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="ts-page__error" role="alert">{{ 'testSteps.form.loadError' | translate }}</p>
  } @else {
    <form class="ts-body" [formGroup]="form" (ngSubmit)="onSave()">
      <div class="ts-main">
        <!-- Inhalt -->
        <div class="ts-card">
          <div class="ts-card__body">
            <to-section-title
              [title]="'testSteps.form.content.title' | translate"
              [subtitle]="roleFields().subtitle | translate"
            />

            <div class="ts-fields">
              <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
                <mat-label>{{ 'testSteps.form.title' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="title"
                  required
                  maxlength="255"
                  [placeholder]="'testSteps.form.titlePlaceholder' | translate"
                />
                @if (form.controls.title.touched && form.controls.title.errors?.['required']) {
                  <mat-error>{{ 'testSteps.form.titleRequired' | translate }}</mat-error>
                }
              </mat-form-field>

              <div class="ts-ablauf">
                <div class="ts-ablauf__head" data-testid="test-step-form-ablauf-header">
                  <div class="ts-ablauf__label-group">
                    <span class="ts-ablauf__label" aria-hidden="true">{{ roleFields().action.label | translate }}</span>
                    <span
                      class="ts-ablauf__required"
                      data-testid="test-step-form-ablauf-required-star"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    class="ts-tcdl-toggle"
                    data-testid="tcdl-toggle"
                    [attr.aria-checked]="showTcdl()"
                    [class.ts-tcdl-toggle--on]="showTcdl()"
                    (click)="toggleTcdl()"
                  >
                    <span class="ts-tcdl-toggle__label">{{ 'testSteps.form.tcdlToggle' | translate }}</span>
                    <span class="ts-tcdl-toggle__track" aria-hidden="true">
                      <span class="ts-tcdl-toggle__thumb"></span>
                    </span>
                  </button>
                </div>

                <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
                  <mat-label class="ts-ablauf__label-sr">{{ roleFields().action.label | translate }}</mat-label>
                  <textarea
                    matInput
                    formControlName="action"
                    data-testid="ablauf-textarea"
                    required
                    [rows]="roleFields().action.rows"
                    [placeholder]="roleFields().action.placeholder | translate"
                  ></textarea>
                  @if (form.controls.action.touched && form.controls.action.errors?.['required']) {
                    <mat-error>
                      {{
                        'testSteps.form.actionRequired' | translate: { field: roleFields().action.label | translate }
                      }}
                    </mat-error>
                  }
                </mat-form-field>

                @if (showTcdl()) {
                  <div class="ts-ablauf__tcdl">
                    <span class="ts-ablauf__label ts-ablauf__label--small">
                      {{ 'testSteps.form.tcdlLabel' | translate }}
                    </span>
                    <app-tcdl-editor
                      [value]="tcdlValue()"
                      [freetext]="form.controls.action.value"
                      [maxLength]="fieldLengths.FREETEXT_MAX"
                      [disabled]="isReadOnly()"
                      [projectId]="projectId"
                      [ariaLabel]="'testSteps.form.tcdlEditorAriaLabel' | translate"
                      (valueChange)="setTcdl($event)"
                      (advanceRequested)="onTcdlAdvance()"
                    />
                  </div>
                }
              </div>

              <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
                <mat-label>{{ roleFields().expectedResult.label | translate }}</mat-label>
                <textarea
                  #expectedResultInput
                  matInput
                  formControlName="expectedResult"
                  data-testid="expected-result-textarea"
                  required
                  [rows]="roleFields().expectedResult.rows"
                  [placeholder]="roleFields().expectedResult.placeholder | translate"
                ></textarea>
                @if (form.controls.expectedResult.touched && form.controls.expectedResult.errors?.['required']) {
                  <mat-error>
                    {{
                      'testSteps.form.expectedResultRequired'
                        | translate: { field: roleFields().expectedResult.label | translate }
                    }}
                  </mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
                <mat-label>{{ roleFields().notes.label | translate }}</mat-label>
                <textarea
                  matInput
                  formControlName="notes"
                  [rows]="roleFields().notes.rows"
                  [placeholder]="roleFields().notes.placeholder | translate"
                ></textarea>
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- Anhänge (Persistenz nur im Edit-Modus; ein Schritt muss erst existieren) -->
        <div class="ts-card">
          <div class="ts-card__body">
            <to-section-title
              [title]="'testSteps.form.attachments.blockTitle' | translate"
              [subtitle]="'testSteps.form.attachments.blockSubtitle' | translate"
            />
            @if (isEdit) {
              <label class="ts-dropzone">
                <input
                  type="file"
                  class="ts-dropzone__input"
                  multiple
                  accept="image/jpeg,image/png,application/pdf"
                  [disabled]="uploading() || isReadOnly()"
                  (change)="onAttachmentsSelected($event)"
                />
                <span class="ts-dropzone__icon" aria-hidden="true">
                  <mat-icon>image</mat-icon>
                </span>
                <span class="ts-dropzone__cta">{{ 'testSteps.form.attachments.uploadCta' | translate }}</span>
                <span class="ts-dropzone__hint">{{ 'testSteps.form.attachments.uploadHint' | translate }}</span>
                <span class="ts-dropzone__formats">{{ 'testSteps.form.attachments.uploadFormats' | translate }}</span>
              </label>
            } @else {
              <div class="ts-dropzone ts-dropzone--disabled" aria-disabled="true">
                <span class="ts-dropzone__icon" aria-hidden="true">
                  <mat-icon>image</mat-icon>
                </span>
                <span class="ts-dropzone__cta">{{ 'testSteps.form.attachments.uploadCta' | translate }}</span>
                <span class="ts-dropzone__hint">{{ 'testSteps.form.attachments.createHint' | translate }}</span>
                <span class="ts-dropzone__formats">{{ 'testSteps.form.attachments.uploadFormats' | translate }}</span>
              </div>
            }
            @if (attachmentError(); as err) {
              <p class="ts-attachment-error" role="alert">{{ err | translate }}</p>
            }
            @if (isEdit && attachments().length) {
              <ul class="ts-attachments">
                @for (file of attachments(); track file.id) {
                  <li class="ts-attachments__item">
                    <span class="ts-attachments__name">{{ file.originalFilename }}</span>
                    <span class="ts-attachments__size">{{ formatFileSize(file.sizeBytes) }}</span>
                    <button
                      type="button"
                      class="ts-attachments__action"
                      [attr.aria-label]="'testSteps.form.attachments.download' | translate"
                      (click)="downloadAttachment(file)"
                    >
                      <mat-icon aria-hidden="true">download</mat-icon>
                    </button>
                    @if (!isReadOnly()) {
                      <button
                        type="button"
                        class="ts-attachments__remove"
                        [attr.aria-label]="'testSteps.form.attachments.remove' | translate"
                        (click)="removeAttachment(file)"
                      >
                        <mat-icon aria-hidden="true">close</mat-icon>
                      </button>
                    }
                  </li>
                }
              </ul>
            }
          </div>
        </div>

        <div class="ts-card">
          <div class="ts-card__body">
            <to-section-title
              [title]="'testSteps.form.usage.blockTitle' | translate"
              [subtitle]="'testSteps.form.usage.blockSubtitle' | translate"
            />
            @if (usageEmpty()) {
              <p class="ts-usage-empty">{{ 'testSteps.form.usage.empty' | translate }}</p>
            } @else {
              <div class="ts-card__section-label">{{ 'testSteps.form.usage.modules' | translate }}</div>
              @if (usageModules().length > 0) {
                <ul class="ts-usage-list" role="list">
                  @for (m of usageModules(); track m.id) {
                    <li>
                      <button type="button" class="ts-usage-row" (click)="onOpenModule(m.id)">
                        <code class="ts-usage-row__id">{{ m.displayId }}</code>
                        <span class="ts-usage-row__title">{{ m.title }}</span>
                        <mat-icon class="ts-usage-row__chevron" aria-hidden="true">chevron_right</mat-icon>
                      </button>
                    </li>
                  }
                </ul>
              } @else {
                <p class="ts-usage-empty">{{ 'testSteps.form.usage.empty' | translate }}</p>
              }
              <div class="ts-card__section-label">{{ 'testSteps.form.usage.testCases' | translate }}</div>
              @if (usageTestCases().length > 0) {
                <ul class="ts-usage-list" role="list">
                  @for (tc of usageTestCases(); track tc.id) {
                    <li class="ts-usage-row ts-usage-row--static">
                      <code class="ts-usage-row__id">{{ tc.displayId }}</code>
                      <span class="ts-usage-row__title">{{ tc.title }}</span>
                    </li>
                  }
                </ul>
              } @else {
                <p class="ts-usage-empty">{{ 'testSteps.form.usage.empty' | translate }}</p>
              }
            }
          </div>
        </div>

        <!-- Details & Historie -->
        <div class="ts-card">
          <div class="ts-card__body">
            <to-section-title
              [title]="'testSteps.form.details.blockTitle' | translate"
              [subtitle]="'testSteps.form.details.blockSubtitle' | translate"
            />
            @if (isEdit && step(); as s) {
              <div class="ts-meta-grid">
                <div class="ts-meta">
                  <div class="ts-meta__label">{{ 'testSteps.form.details.created' | translate }}</div>
                  <div class="ts-meta__row">
                    <span class="ts-meta__icon ts-meta__icon--created" aria-hidden="true">
                      <mat-icon>schedule</mat-icon>
                    </span>
                    <span class="ts-meta__value">{{ s.createdAt | date: 'medium' }}</span>
                  </div>
                </div>
                <div class="ts-meta">
                  <div class="ts-meta__label">{{ 'testSteps.form.details.updated' | translate }}</div>
                  <div class="ts-meta__row">
                    <span class="ts-meta__icon ts-meta__icon--updated" aria-hidden="true">
                      <mat-icon>history</mat-icon>
                    </span>
                    <span class="ts-meta__value">{{ s.updatedAt | date: 'medium' }}</span>
                  </div>
                </div>
              </div>
              <app-test-step-activity-feed [projectId]="projectId" [stepId]="stepId" />
            } @else {
              <p class="ts-usage-empty">{{ 'testSteps.form.details.created' | translate }} — —</p>
            }
          </div>
        </div>
      </div>

      <div class="ts-sidebar">
        <!-- Eigenschaften -->
        <div class="ts-card ts-properties">
          <div class="ts-card__body">
            <div class="ts-card__inline-header">{{ 'testSteps.form.properties.blockTitle' | translate }}</div>

            <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
              <mat-label>{{ 'testSteps.form.properties.role' | translate }}</mat-label>
              <mat-select formControlName="stepRole" required>
                @for (role of roleOptions; track role) {
                  <mat-option [value]="role">{{ 'testSteps.form.roles.' + role | translate }}</mat-option>
                }
              </mat-select>
              @if (form.controls.stepRole.touched && form.controls.stepRole.errors?.['required']) {
                <mat-error>{{ 'testSteps.form.properties.roleRequired' | translate }}</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
              <mat-label>{{ 'testSteps.form.properties.category' | translate }}</mat-label>
              <mat-select formControlName="category">
                <mat-option [value]="''">{{ 'testSteps.form.properties.categoryNone' | translate }}</mat-option>
                @for (cat of categoryOptions; track cat) {
                  <mat-option [value]="cat">{{ 'testSteps.category.' + cat | translate }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
              <mat-label>{{ 'testSteps.form.properties.status' | translate }}</mat-label>
              <mat-select formControlName="status" required>
                @for (status of statusOptions; track status) {
                  <mat-option [value]="status">{{ 'testSteps.status.' + status | translate }}</mat-option>
                }
              </mat-select>
              @if (form.controls.status.touched && form.controls.status.errors?.['required']) {
                <mat-error>{{ 'testSteps.form.properties.statusRequired' | translate }}</mat-error>
              }
            </mat-form-field>

            <app-person-select
              class="ts-field"
              formControlName="assigneeUserId"
              [label]="'testSteps.form.properties.author' | translate"
              [options]="personOptions()"
              [allowNone]="false"
              [required]="true"
            ></app-person-select>
            @if (form.controls.assigneeUserId.hasError('required') && form.controls.assigneeUserId.touched) {
              <p class="ts-field-error" role="alert">{{ 'testSteps.form.properties.authorRequired' | translate }}</p>
            }

            <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
              <mat-label>{{ 'testSteps.form.properties.tags' | translate }}</mat-label>
              <mat-chip-grid #tagGrid [attr.aria-label]="'testSteps.form.properties.tags' | translate">
                @for (tag of form.controls.tags.value; track tag) {
                  <mat-chip-row (removed)="removeTag(tag)">
                    {{ tag }}
                    <button matChipRemove [attr.aria-label]="'testSteps.form.attachments.remove' | translate">
                      <mat-icon aria-hidden="true">cancel</mat-icon>
                    </button>
                  </mat-chip-row>
                }
                <input
                  [matChipInputFor]="tagGrid"
                  [matChipInputSeparatorKeyCodes]="[13, 188]"
                  [placeholder]="'testSteps.form.properties.tagsPlaceholder' | translate"
                  (matChipInputTokenEnd)="addTag($event)"
                />
              </mat-chip-grid>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
              <mat-label>{{ 'testSteps.form.properties.storyTicket' | translate }}</mat-label>
              <input
                matInput
                formControlName="storyTicket"
                [placeholder]="'testSteps.form.properties.storyTicketPlaceholder' | translate"
              />
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always" class="ts-field ts-field--uppercase-label">
              <mat-label>{{ 'testSteps.form.properties.bugTicket' | translate }}</mat-label>
              <input
                matInput
                formControlName="bugTicket"
                [placeholder]="'testSteps.form.properties.bugTicketPlaceholder' | translate"
              />
            </mat-form-field>
          </div>
        </div>
      </div>

      @if (saveError()) {
        <p class="ts-page__error ts-page__error--save" role="alert">{{ 'testSteps.form.saveError' | translate }}</p>
      }
    </form>
  }
</div>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [title]="'common.unsavedChanges.title' | translate"
  [text]="'testSteps.form.unsavedLeave' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
